import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DigitalTwin } from 'types/graphql-apollo-v2';
import {
  useCreateConsistencySchemaFilterMutation,
  GQLOperations,
} from 'types/dt-graphql-apollo-v2';
import { notification } from 'antd';
import { useSchemaData } from './useSchemaData';
import { LogicalRule, logicalRuleToFilter } from 'utils/rules';
import {
  emptyLogicalRule,
  hasIncompleteRelationalRule,
} from 'utils/nested-conditions';
import { ConsistencyCollectionView } from './types';

const MAX_STEP = 4;

type UseNewConsistencySchemaFilterDrawerParams = {
  visible: boolean;
  onClose: VoidFunction;
  collection: ConsistencyCollectionView | null;
  digitalTwin: DigitalTwin;
};

export const useNewConsistencySchemaFilterDrawer = ({
  visible,
  onClose,
  collection,
  digitalTwin,
}: UseNewConsistencySchemaFilterDrawerParams) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [labelListInput, setLabelListInput] = useState<string[]>([]);
  const [selectedSchemaName, setSelectedSchemaName] = useState('');
  const [selectedSchemaType, setSelectedSchemaType] = useState('');
  const [filtersTree, setFiltersTree] = useState<LogicalRule>(emptyLogicalRule);

  const { schemas, loading: loadingSchemas } = useSchemaData(digitalTwin);

  const [createSchemaFilter] = useCreateConsistencySchemaFilterMutation({
    refetchQueries: [GQLOperations.Query.GetConsistencyCollections],
  });

  // Derived data
  const selectedSchema = schemas.find(
    (s) => s.schemaCollection.name === selectedSchemaName
  );

  // Validation
  const hasInvalidName = !nameInput.trim();
  const hasInvalidSchema = !selectedSchemaName;
  const hasInvalidSchemaType = !selectedSchemaType;
  const hasIncompleteFilter = hasIncompleteRelationalRule(filtersTree);
  const hasInvalidValues =
    hasInvalidName ||
    hasInvalidSchema ||
    hasInvalidSchemaType ||
    hasIncompleteFilter;

  const onResetDrawer = () => {
    setNameInput('');
    setDescriptionInput('');
    setLabelListInput([]);
    setSelectedSchemaName('');
    setSelectedSchemaType('');
    setFiltersTree(emptyLogicalRule);
    setCurrentStep(0);
  };

  useEffect(() => {
    if (visible) {
      onResetDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, MAX_STEP));
  };

  const onCreateSchemaFilter = async () => {
    if (creating || hasInvalidValues || !collection) return;
    setCreating(true);

    try {
      await createSchemaFilter({
        variables: {
          id: uuidv4(),
          name: nameInput.trim(),
          description: descriptionInput || undefined,
          labels: labelListInput.length > 0 ? labelListInput : undefined,
          schemaType: selectedSchemaType,
          schemaCollectionId: selectedSchema?.schemaCollection.externalId ?? '',
          filters: JSON.stringify(
            logicalRuleToFilter(
              filtersTree,
              selectedSchema?.types?.find((t) => t.name === selectedSchemaType)
                ?.properties ?? []
            )
          ),
          projectId: collection.externalId,
          collectionId: collection.externalId,
        },
      });

      notification.success({
        message: 'Schema filter created',
        description: `Successfully created the schema filter "${nameInput.trim()}"`,
      });

      onResetDrawer();
      onClose();
    } catch (err: unknown) {
      notification.error({
        message: 'Could not create schema filter',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setCreating(false);
    }
  };

  const onCancelHandler = () => {
    if (creating) return;
    setCurrentStep(0);
    onClose();
  };

  const handleSchemaChange = (schemaName: string) => {
    setSelectedSchemaName(schemaName);
    setSelectedSchemaType('');
  };

  return {
    // State
    currentStep,
    setCurrentStep,
    creating,
    nameInput,
    setNameInput,
    descriptionInput,
    setDescriptionInput,
    labelListInput,
    setLabelListInput,
    selectedSchemaName,
    selectedSchemaType,
    setSelectedSchemaType,
    filtersTree,
    setFiltersTree,

    // Data
    schemas,
    loadingSchemas,
    selectedSchema,

    // Validation
    hasInvalidName,
    hasInvalidSchema,
    hasInvalidSchemaType,
    hasIncompleteFilter,
    hasInvalidValues,

    // Handlers
    nextStep,
    onCreateSchemaFilter,
    onCancelHandler,
    handleSchemaChange,
  };
};
